'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

type Frequency = 'one-time' | 'monthly' | 'quarterly';

interface PlanFrequency {
  label: string;
  price: string;
  priceNote: string;
}

interface Plan {
  id: string;
  name: string;
  tagline: string;
  icon: string;
  highlight: boolean;
  badge?: string;
  includes: string[];
  frequencies: Record<Frequency, PlanFrequency>;
  isTrashBin: boolean;
  isDriveway: boolean;
  isWindow: boolean;
  isHouseWash: boolean;
}

const plans: Plan[] = [
  {
    id: 'trash-bin-care',
    name: 'Trash Bin Care',
    tagline: 'Keep your bins fresh and odor-free',
    icon: '🗑️',
    highlight: false,
    includes: ['Trash Bin Deep Cleaning', 'Sanitization & Deodorizing', 'Exterior Rinse'],
    frequencies: {
      'one-time': { label: 'One-Time', price: '$35', priceNote: 'per bin' },
      'monthly': { label: 'Monthly', price: '$25', priceNote: 'per bin/month' },
      'quarterly': { label: 'Quarterly', price: '$30', priceNote: 'per bin/quarter' },
    },
    isTrashBin: true,
    isDriveway: false,
    isWindow: false,
    isHouseWash: false,
  },
  {
    id: 'exterior-essentials',
    name: 'Exterior Essentials Plan',
    tagline: 'Complete curb appeal maintenance',
    icon: '✨',
    highlight: true,
    badge: '⭐ Most Popular',
    includes: ['Driveway Cleaning', 'Window Cleaning', 'Trash Bin Cleaning'],
    frequencies: {
      'one-time': { label: 'One-Time', price: '$349', priceNote: 'starting price' },
      'monthly': { label: 'Monthly', price: '$249', priceNote: 'per month' },
      'quarterly': { label: 'Quarterly', price: '$299', priceNote: 'per quarter' },
    },
    isTrashBin: true,
    isDriveway: true,
    isWindow: true,
    isHouseWash: false,
  },
  {
    id: 'complete-property-care',
    name: 'Complete Property Care',
    tagline: 'Total exterior cleaning solution',
    icon: '🏠',
    highlight: false,
    includes: ['Full House Wash', 'Driveway Cleaning', 'Window Cleaning', 'Trash Bin Cleaning'],
    frequencies: {
      'one-time': { label: 'One-Time', price: '$549', priceNote: 'starting price' },
      'monthly': { label: 'Monthly', price: '$399', priceNote: 'per month' },
      'quarterly': { label: 'Quarterly', price: '$449', priceNote: 'per quarter' },
    },
    isTrashBin: true,
    isDriveway: true,
    isWindow: true,
    isHouseWash: true,
  },
];

export default function RGPSPackages() {
  const router = useRouter();
  const [selectedFreq, setSelectedFreq] = useState<Record<string, Frequency>>({
    'trash-bin-care': 'monthly',
    'exterior-essentials': 'monthly',
    'complete-property-care': 'monthly',
  });

  const handleChoosePlan = (plan: Plan) => {
    const freq = selectedFreq[plan.id];
    const freqData = plan.frequencies[freq];
    const params = new URLSearchParams({
      service: plan.id,
      label: plan.name,
      frequency: freqData.label,
      price: `${freqData.price} ${freqData.priceNote}`,
      includes: plan.includes.join('|'),
      trashBin: plan.isTrashBin ? '1' : '0',
      driveway: plan.isDriveway ? '1' : '0',
      window: plan.isWindow ? '1' : '0',
      houseWash: plan.isHouseWash ? '1' : '0',
    });
    router.push(`/request?${params.toString()}`);
  };

  const freqTabs: { key: Frequency; label: string }[] = [
    { key: 'one-time', label: 'One-Time' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
  ];

  return (
    <section id="packages" className="bg-white py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-teal-light text-teal font-bold text-sm px-4 py-2 rounded-full mb-4">
            Property Care Plans
          </div>
          <h2 className="text-dark-text font-bold mb-4" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Recurring Maintenance Plans
          </h2>
          <p className="text-gray-text text-lg max-w-2xl mx-auto">
            Keep your property looking its best year-round with our flexible maintenance plans. Choose your frequency and save.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan) => {
            const freq = selectedFreq[plan.id];
            const freqData = plan.frequencies[freq];
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl flex flex-col transition-all duration-300 overflow-hidden ${
                  plan.highlight
                    ? 'bg-gradient-to-br from-teal to-teal-dark text-white shadow-teal-lg scale-[1.02] hover:-translate-y-1'
                    : 'bg-white border-2 border-gray-100 shadow-card hover:shadow-card-lg hover:-translate-y-1'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber text-dark-text text-xs font-bold px-4 py-1.5 rounded-full shadow-amber whitespace-nowrap z-10">
                    {plan.badge}
                  </div>
                )}

                <div className="p-7 flex flex-col flex-1">
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${plan.highlight ? 'bg-white/20' : 'bg-teal-light'}`}>
                      {plan.icon}
                    </div>
                    <div>
                      <div className={`font-bold text-base leading-tight ${plan.highlight ? 'text-white' : 'text-dark-text'}`}>{plan.name}</div>
                      <div className={`text-xs mt-0.5 ${plan.highlight ? 'text-white/75' : 'text-gray-text'}`}>{plan.tagline}</div>
                    </div>
                  </div>

                  {/* Frequency Tabs */}
                  <div className={`flex rounded-xl p-1 mb-5 ${plan.highlight ? 'bg-white/15' : 'bg-gray-100'}`}>
                    {freqTabs.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setSelectedFreq(p => ({ ...p, [plan.id]: tab.key }))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          freq === tab.key
                            ? plan.highlight
                              ? 'bg-white text-teal shadow-sm'
                              : 'bg-white text-teal shadow-sm'
                            : plan.highlight
                              ? 'text-white/70 hover:text-white' :'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="mb-5">
                    <div className={`text-3xl font-bold ${plan.highlight ? 'text-white' : 'text-dark-text'}`}>
                      {freqData.price}
                    </div>
                    <div className={`text-sm ${plan.highlight ? 'text-white/75' : 'text-gray-text'}`}>
                      {freqData.priceNote}
                    </div>
                  </div>

                  {/* Includes */}
                  <ul className="flex-1 space-y-2.5 mb-6">
                    {plan.includes.map((item) => (
                      <li key={item} className="flex items-center gap-2.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${plan.highlight ? 'bg-white/20' : 'bg-teal-light'}`}>
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke={plan.highlight ? 'white' : '#0ABFBC'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className={`text-sm font-medium ${plan.highlight ? 'text-white/90' : 'text-gray-text'}`}>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    onClick={() => handleChoosePlan(plan)}
                    className={`w-full font-bold py-3.5 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] ${
                      plan.highlight
                        ? 'bg-white text-teal hover:bg-white/90' :'bg-teal hover:bg-teal-dark text-white'
                    }`}
                  >
                    Choose Plan
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="mt-10 text-center max-w-3xl mx-auto">
          <p className="text-xs text-gray-400 leading-relaxed">
            All prices shown are starting prices. Final pricing may vary depending on the size, condition, accessibility, and specific needs of your property.{' '}
            <span className="text-gray-500 font-medium">No payment is required to submit a request.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
